import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { usePermissions } from "@/contexts/PermissionContext";
import AccessDenied from "@/components/AccessDenied";

/**
 * ProtectedRoute component that checks user permissions before rendering a page
 * Redirects unauthorized users to Dashboard
 * Shows loading state while permissions are being fetched
 */
export default function ProtectedRoute({ 
  element, 
  requiredPage = null,
  allowAdmin = false 
}) {
  const { hasPageAccess, userRoles, permissionsLoaded } = usePermissions();
  const [isDelayed, setIsDelayed] = useState(false);

  useEffect(() => {
    // Show "taking longer than expected" message after 5 seconds
    const timer = setTimeout(() => {
      if (!permissionsLoaded) {
        setIsDelayed(true);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [permissionsLoaded]);

  console.log("ProtectedRoute Debug:", {
    requiredPage,
    permissionsLoaded,
    rolesCount: userRoles?.length || 0,
    roles: userRoles
  });

  // Loading state - show loading spinner while permissions load
  if (!permissionsLoaded) {
    console.log("ProtectedRoute: Still loading, showing spinner");
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#A80097] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-400 text-sm">Loading permissions...</p>
          {isDelayed && (
            <p className="text-zinc-500 text-xs mt-4 max-w-xs">
              This is taking longer than expected. Check browser console for errors or try refreshing the page.
            </p>
          )}
        </div>
      </div>
    );
  }

  // If user has NO roles assigned, show Access Denied
  if (!userRoles || userRoles.length === 0) {
    console.log("ProtectedRoute: User has no roles, showing AccessDenied");
    return <AccessDenied />;
  }

  // Check page-specific access
  if (requiredPage) {
    const hasAccess = hasPageAccess(requiredPage);
    console.log(`ProtectedRoute: Checking ${requiredPage}, hasAccess=${hasAccess}`);
    if (hasAccess) {
      console.log(`ProtectedRoute: Access granted to ${requiredPage}`);
      return element;
    } else {
      // Redirect to Dashboard if no access
      console.warn(`ProtectedRoute: User does not have access to ${requiredPage}. Redirecting to Dashboard.`);
      return <Navigate to="/" replace />;
    }
  }

  // If no requiredPage specified, allow access (unrestricted page)
  return element;
}
